import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { FaSearch, FaUsers, FaBan, FaCheck, FaEnvelope, FaPhone, FaSpinner } from 'react-icons/fa'
import { Card, Spinner, Banner, PageHeader, EmptyState, Modal, Toast, useToast } from '../components/ui'
import { money, number, relativeTime, formatDate } from '../lib/format'
import { STATUS } from '../lib/viz'
import * as api from '../lib/api'

/**
 * Who has an account, and what they have spent.
 *
 * Sorted by total spent rather than alphabetically, because the question this
 * page is usually opened to answer is "who are our good customers" — and a
 * shop with a few hundred names is not a list anyone reads A to Z.
 *
 * Blocking stops an account placing orders; it does not stop them signing in.
 * A blocked person still sees their own history, which is what you want for a
 * payment dispute — cutting off their record makes the argument worse, not
 * better.
 */
const CustomersPage = () => {
  const [customers, setCustomers] = useState([])
  const [state, setState] = useState({ loading: true, error: null })
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('spent')
  const [editing, setEditing] = useState(null)
  const [busy, setBusy] = useState(false)
  const { toast, notify, notifyError, dismiss } = useToast()

  const load = async () => {
    setState({ loading: true, error: null })
    try {
      setCustomers(await api.getCustomers())
      setState({ loading: false, error: null })
    } catch (err) {
      setState({ loading: false, error: err })
    }
  }

  useEffect(() => {
    load()
  }, [])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    const sorters = {
      spent: (a, b) => Number(b.total_spent) - Number(a.total_spent),
      orders: (a, b) => Number(b.order_count) - Number(a.order_count),
      recent: (a, b) => new Date(b.created_at) - new Date(a.created_at),
      name: (a, b) =>
        `${a.first_name ?? ''} ${a.last_name ?? ''}`.trim().localeCompare(
          `${b.first_name ?? ''} ${b.last_name ?? ''}`.trim(),
        ),
    }
    return customers
      .filter((c) => {
        if (!q) return true
        return (
          (c.email ?? '').toLowerCase().includes(q) ||
          `${c.first_name ?? ''} ${c.last_name ?? ''}`.toLowerCase().includes(q) ||
          (c.phone ?? '').includes(q)
        )
      })
      .sort(sorters[sort])
  }, [customers, search, sort])

  const totals = useMemo(
    () => ({
      count: customers.length,
      withOrders: customers.filter((c) => Number(c.order_count) > 0).length,
      revenue: customers.reduce((n, c) => n + Number(c.total_spent || 0), 0),
      marketing: customers.filter((c) => c.marketing_opt_in).length,
    }),
    [customers],
  )

  const toggleBlock = async (customer) => {
    setBusy(true)
    try {
      await api.setCustomerBlocked(customer.id, !customer.is_blocked)
      notify(
        customer.is_blocked
          ? `${customer.email} can place orders again.`
          : `${customer.email} is blocked from placing orders.`,
      )
      load()
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(false)
    }
  }

  const saveNotes = async () => {
    setBusy(true)
    try {
      await api.saveCustomerNotes(editing.id, editing.admin_notes ?? '')
      notify('Note saved.')
      setEditing(null)
      load()
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(false)
    }
  }

  if (state.loading) return <Spinner label="Loading customers…" />
  if (state.error) {
    return <Banner tone="error"><strong>Could not load customers.</strong> {state.error.message}</Banner>
  }

  return (
    <div>
      <PageHeader title="Customers" subtitle={`${number(totals.count)} registered accounts`} />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Tile label="Accounts" value={number(totals.count)} />
        <Tile label="Have ordered" value={number(totals.withOrders)} />
        <Tile label="Revenue from accounts" value={money(totals.revenue)} />
        <Tile label="Opted into marketing" value={number(totals.marketing)} />
      </div>

      <Card bodyClassName="">
        <div className="flex flex-wrap items-center gap-2 border-b border-hairline p-3">
          <div className="relative min-w-[200px] flex-1">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[11px] text-ink-muted" aria-hidden="true" />
            <input
              className="input pl-8"
              placeholder="Search name, email or phone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search customers"
            />
          </div>
          <select className="input input-sm w-auto" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort customers">
            <option value="spent">Most spent</option>
            <option value="orders">Most orders</option>
            <option value="recent">Newest</option>
            <option value="name">Name</option>
          </select>
        </div>

        {visible.length === 0 ? (
          <EmptyState icon={FaUsers} title={customers.length === 0 ? 'No accounts yet' : 'No matches'}>
            {customers.length === 0
              ? 'Shoppers who register on the site appear here. Guest checkout does not create an account.'
              : 'Try a different search.'}
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Contact</th>
                  <th>Location</th>
                  <th className="num">Orders</th>
                  <th className="num">Spent</th>
                  <th>Last order</th>
                  <th>Joined</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => (
                  <tr key={c.id} className={c.is_blocked ? 'opacity-60' : ''}>
                    <td>
                      <div className="font-medium text-ink">
                        {[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}
                        {c.is_blocked && (
                          <span className="ml-2 text-2xs font-semibold uppercase" style={{ color: STATUS.critical }}>
                            Blocked
                          </span>
                        )}
                      </div>
                      {c.admin_notes && (
                        <div className="max-w-[220px] truncate text-2xs text-ink-muted" title={c.admin_notes}>
                          {c.admin_notes}
                        </div>
                      )}
                    </td>
                    <td className="text-2xs">
                      <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-ink-secondary hover:text-brand-red">
                        <FaEnvelope className="text-[9px]" aria-hidden="true" />
                        <span className="max-w-[180px] truncate">{c.email}</span>
                      </a>
                      {c.phone && (
                        <a href={`tel:${c.phone.replace(/\s/g, '')}`} className="mt-0.5 flex items-center gap-1.5 text-ink-muted hover:text-brand-red">
                          <FaPhone className="text-[9px]" aria-hidden="true" />
                          {c.phone}
                        </a>
                      )}
                    </td>
                    <td className="text-2xs text-ink-secondary">
                      {[c.city, c.province].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="num">{number(c.order_count)}</td>
                    <td className="num font-semibold">{money(c.total_spent)}</td>
                    <td className="text-2xs text-ink-secondary">
                      {c.last_order_at ? relativeTime(c.last_order_at) : '—'}
                    </td>
                    <td className="text-2xs text-ink-muted">{formatDate(c.created_at)}</td>
                    <td className="whitespace-nowrap text-right">
                      <button onClick={() => setEditing({ ...c })} className="btn-subtle btn-sm" title="Add a note">
                        Note
                      </button>
                      <button
                        onClick={() => toggleBlock(c)}
                        disabled={busy}
                        className="btn-subtle btn-sm"
                        title={c.is_blocked ? 'Allow orders again' : 'Block from ordering'}
                      >
                        {c.is_blocked ? <FaCheck /> : <FaBan />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={`Note about ${editing?.email ?? ''}`}
        footer={
          <>
            <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
            <button onClick={saveNotes} disabled={busy} className="btn-primary">
              {busy && <FaSpinner className="animate-spin" />}
              Save
            </button>
          </>
        }
      >
        <label className="label" htmlFor="notes">Internal note</label>
        <textarea
          id="notes"
          rows={4}
          className="input"
          value={editing?.admin_notes ?? ''}
          onChange={(e) => setEditing({ ...editing, admin_notes: e.target.value })}
          placeholder="e.g. Prefers collection over delivery"
        />
        <p className="field-hint">Only staff can see this. The customer never does.</p>
      </Modal>

      <Toast toast={toast} onDismiss={dismiss} />
    </div>
  )
}

const Tile = ({ label, value }) => (
  <div className="card p-3.5">
    <div className="text-2xs font-medium uppercase tracking-[0.08em] text-ink-muted">{label}</div>
    <div className="mt-1 text-xl font-semibold text-ink">{value}</div>
  </div>
)

export default CustomersPage
